import { getAllTemplates, type GameTemplate } from "@yamber/rules-engine";
import { useState } from "react";
import { useTranslation } from "../stores/settingsStore";

interface TemplateSelectorProps {
  onSelectTemplate: (templateId: string) => void;
}

// Template metadata for display
const TEMPLATE_META: Record<string, {
  icon: string;
  difficulty: "easy" | "medium" | "hard" | "expert";
  estimatedTime: string;
  recommended?: boolean;
  color: string;
}> = {
  classic: {
    icon: "🎲",
    difficulty: "medium",
    estimatedTime: "15-20 min",
    recommended: true,
    color: "#4f46e5",
  },
  solo: {
    icon: "🎯",
    difficulty: "easy",
    estimatedTime: "5-10 min",
    color: "#10b981",
  },
  fast: {
    icon: "⚡",
    difficulty: "medium",
    estimatedTime: "10-15 min",
    color: "#f59e0b",
  },
  kids: {
    icon: "🧒",
    difficulty: "easy",
    estimatedTime: "5-8 min",
    color: "#ec4899",
  },
  quick: {
    icon: "🚀",
    difficulty: "easy",
    estimatedTime: "3-5 min",
    color: "#06b6d4",
  },
  challenge: {
    icon: "🏆",
    difficulty: "expert",
    estimatedTime: "25-35 min",
    color: "#dc2626",
  },
  pro: {
    icon: "🎲",
    difficulty: "hard",
    estimatedTime: "30-45 min",
    color: "#7c3aed",
  },
};

const DIFFICULTY_LABELS = {
  easy: { label: "Easy", stars: 1 },
  medium: { label: "Medium", stars: 2 },
  hard: { label: "Hard", stars: 3 },
  expert: { label: "Expert", stars: 4 },
};

export function TemplateSelector({ onSelectTemplate }: TemplateSelectorProps) {
  useTranslation(); // Initialize translations
  const templates = getAllTemplates();
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [filterDifficulty, setFilterDifficulty] = useState<string | null>(null);

  // Filter templates
  const filteredTemplates = filterDifficulty
    ? templates.filter((t) => TEMPLATE_META[t.id]?.difficulty === filterDifficulty)
    : templates;

  // Sort: recommended first, then by difficulty
  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    const metaA = TEMPLATE_META[a.id];
    const metaB = TEMPLATE_META[b.id];
    if (metaA?.recommended && !metaB?.recommended) return -1;
    if (!metaA?.recommended && metaB?.recommended) return 1;
    const diffOrder = { easy: 0, medium: 1, hard: 2, expert: 3 };
    return (diffOrder[metaA?.difficulty || "medium"] || 0) - (diffOrder[metaB?.difficulty || "medium"] || 0);
  });

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplate(templateId);
  };

  const handleConfirmSelection = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate);
    }
  };

  const selectedTemplateData = selectedTemplate
    ? templates.find((t) => t.id === selectedTemplate)
    : null;

  const selectedMeta = selectedTemplate ? TEMPLATE_META[selectedTemplate] : null;

  return (
    <section className="template-selector" aria-labelledby="template-heading">
      <div className="template-selector__header">
        <h2 id="template-heading" className="template-selector__title">Choose a Game Mode</h2>
        <div className="template-filter" role="group" aria-label="Filter by difficulty">
          <button
            type="button"
            className={`filter-btn ${filterDifficulty === null ? "active" : ""}`}
            onClick={() => setFilterDifficulty(null)}
            aria-pressed={filterDifficulty === null}
          >
            All
          </button>
          {Object.entries(DIFFICULTY_LABELS).map(([key, { label }]) => (
            <button
              key={key}
              type="button"
              className={`filter-btn ${filterDifficulty === key ? "active" : ""}`}
              onClick={() => setFilterDifficulty(key)}
              aria-pressed={filterDifficulty === key}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="template-selector__grid" role="listbox" aria-label="Game modes">
        {sortedTemplates.map((template: GameTemplate) => {
          const meta = TEMPLATE_META[template.id] || {
            icon: "🎲",
            difficulty: "medium" as const,
            estimatedTime: "10-15 min",
            color: "#6b7280",
          };
          const difficultyInfo = DIFFICULTY_LABELS[meta.difficulty];

          return (
            <button
              key={template.id}
              type="button"
              role="option"
              aria-selected={selectedTemplate === template.id}
              className={`template-card ${selectedTemplate === template.id ? "selected" : ""} ${meta.recommended ? "recommended" : ""}`}
              onClick={() => handleSelectTemplate(template.id)}
              style={{ "--template-color": meta.color } as React.CSSProperties}
              aria-label={`${template.name}. ${difficultyInfo.label} difficulty. ${meta.estimatedTime}. ${template.columns.length} columns, ${template.categories.length} categories${meta.recommended ? ". Recommended" : ""}`}
            >
              {meta.recommended && (
                <span className="template-card__badge" aria-hidden="true">Recommended</span>
              )}
              <div className="template-card__icon" aria-hidden="true">{meta.icon}</div>
              <h3 className="template-card__name">{template.name}</h3>
              <p className="template-card__description">{template.description}</p>

              <div className="template-card__stats" aria-hidden="true">
                <div className="stat">
                  <span className="stat-icon">📋</span>
                  <span className="stat-value">{template.columns.length}</span>
                  <span className="stat-label">columns</span>
                </div>
                <div className="stat">
                  <span className="stat-icon">🎯</span>
                  <span className="stat-value">{template.categories.length}</span>
                  <span className="stat-label">categories</span>
                </div>
                <div className="stat">
                  <span className="stat-icon">⏱️</span>
                  <span className="stat-value">{meta.estimatedTime}</span>
                </div>
              </div>

              <div className="template-card__difficulty" aria-hidden="true">
                <span className="difficulty-label">{difficultyInfo.label}</span>
                <span className="difficulty-stars">
                  {"★".repeat(difficultyInfo.stars)}
                  {"☆".repeat(4 - difficultyInfo.stars)}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Template Preview Panel */}
      {selectedTemplateData && selectedMeta && (
        <aside className="template-preview" aria-label={`${selectedTemplateData.name} details`} aria-live="polite">
          <div className="template-preview__header">
            <div className="preview-icon" style={{ background: selectedMeta.color }} aria-hidden="true">
              {selectedMeta.icon}
            </div>
            <div className="preview-info">
              <h3>{selectedTemplateData.name}</h3>
              <p>{selectedTemplateData.description}</p>
            </div>
          </div>

          <div className="template-preview__details">
            <div className="preview-section">
              <h4>Columns</h4>
              <div className="columns-preview">
                {selectedTemplateData.columns.map((col) => (
                  <div key={col.id} className="column-chip">
                    <span className="column-name">{col.name}</span>
                    <span className="column-desc">{col.description}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="preview-section">
              <h4>Categories ({selectedTemplateData.categories.length})</h4>
              <div className="categories-preview">
                {selectedTemplateData.categories.map((cat) => (
                  <span key={cat} className="category-chip">
                    {selectedTemplateData.locale.en[cat] || cat}
                  </span>
                ))}
              </div>
            </div>

            <div className="preview-section">
              <h4>Scoring Bonuses</h4>
              <div className="scoring-preview">
                {selectedTemplateData.scoring.upperBonusPoints > 0 && (
                  <div className="scoring-item">
                    <span>Upper Section Bonus</span>
                    <span>+{selectedTemplateData.scoring.upperBonusPoints} pts (if ≥{selectedTemplateData.scoring.upperBonusThreshold})</span>
                  </div>
                )}
                {selectedTemplateData.scoring.yambBonus > 0 && (
                  <div className="scoring-item">
                    <span>Yamb Bonus</span>
                    <span>+{selectedTemplateData.scoring.yambBonus} pts</span>
                  </div>
                )}
                {selectedTemplateData.scoring.pokerBonus > 0 && (
                  <div className="scoring-item">
                    <span>Poker Bonus</span>
                    <span>+{selectedTemplateData.scoring.pokerBonus} pts</span>
                  </div>
                )}
                {selectedTemplateData.scoring.fullHouseBonus > 0 && (
                  <div className="scoring-item">
                    <span>Full House Bonus</span>
                    <span>+{selectedTemplateData.scoring.fullHouseBonus} pts</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            type="button"
            className="template-preview__start-btn"
            onClick={handleConfirmSelection}
            aria-label={`Start playing ${selectedTemplateData.name}`}
          >
            Start {selectedTemplateData.name}
          </button>
        </aside>
      )}
    </section>
  );
}
